import type { Metadata } from ".";
import { getTemplateColors } from "./template-colors";

export const paletteFamilies = [
  { id: "ocean", hue: 213, secondaryHue: 175, saturation: 0.55 },
  { id: "forest", hue: 153, secondaryHue: 44, saturation: 0.35 },
  { id: "terracotta", hue: 16, secondaryHue: 35, saturation: 0.5 },
  { id: "plum", hue: 308, secondaryHue: 22, saturation: 0.34 },
  { id: "indigo", hue: 248, secondaryHue: 185, saturation: 0.45 },
  { id: "slate", hue: 212, secondaryHue: 212, saturation: 0.12 },
] as const;

export type PaletteFamily = (typeof paletteFamilies)[number]["id"];

const isHex = (value: unknown): value is string =>
  typeof value === "string" && /^#[\da-f]{6}$/i.test(value);

const toHsl = (hex: string) => {
  const [r, g, b] = [1, 3, 5].map((offset) => parseInt(hex.slice(offset, offset + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const lightness = (max + min) / 2;
  if (delta === 0) return { hue: 0, saturation: 0, lightness };
  const sector =
    max === r ? (g - b) / delta : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4;
  return {
    hue: (sector * 60 + 360) % 360,
    saturation: delta / (1 - Math.abs(2 * lightness - 1)),
    lightness,
  };
};

const fromHsl = (hue: number, saturation: number, lightness: number) => {
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const offset = lightness - chroma / 2;
  const sectors = [
    [chroma, x, 0],
    [x, chroma, 0],
    [0, chroma, x],
    [0, x, chroma],
    [x, 0, chroma],
    [chroma, 0, x],
  ];
  return `#${sectors[Math.floor(hue / 60)]
    .map((channel) =>
      Math.round((channel + offset) * 255)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
};

export const getOriginalTemplateColors = (template: string): Record<string, string> =>
  Object.fromEntries(getTemplateColors(template).map(({ key, color }) => [key, color]));

export const resolveTemplateColors = (
  template: string,
  theme: Metadata["theme"],
): Record<string, string> => {
  const colors = getOriginalTemplateColors(template);
  if (template === "blue-wave" && isHex(theme.primary)) colors["1c4165"] = theme.primary;
  if (theme.palette?.template !== template) return colors;
  for (const key of Object.keys(colors)) {
    const value = theme.palette.colors[key];
    if (isHex(value)) colors[key] = value;
  }
  return colors;
};

export const createTemplatePalette = (template: string, family: PaletteFamily) => {
  const palette = paletteFamilies.find(({ id }) => id === family)!;
  const slots = getTemplateColors(template);
  const anchor =
    slots.find(({ role }) => role === "accent") ?? slots.find(({ key }) => key !== "paper");
  const anchorHue = toHsl(anchor?.color ?? "#333333").hue;
  const monochrome = slots.every(({ color }) => toHsl(color).saturation < 0.09);
  return Object.fromEntries(
    slots.map(({ key, color }) => {
      const { hue, saturation, lightness } = toHsl(color);
      // Keep neutral text and paper unchanged; preserve the template's light/dark hierarchy.
      if (key === "paper" || (saturation < 0.09 && !monochrome)) return [key, color];
      const distance = Math.abs(((hue - anchorHue + 540) % 360) - 180);
      const targetHue = distance > 45 ? palette.secondaryHue : palette.hue;
      return [
        key,
        fromHsl(
          targetHue,
          Math.min(0.75, saturation * 0.45 + palette.saturation * 0.55),
          lightness,
        ),
      ];
    }),
  );
};

export const getTemplateColorVariables = (template: string, theme: Metadata["theme"]) =>
  Object.fromEntries(
    Object.entries(resolveTemplateColors(template, theme)).map(([key, value]) => [
      key === "paper" ? "--cv-paper" : `--cv-color-${key}`,
      value,
    ]),
  );
